import ScrollReveal from '@/components/ScrollReveal';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Heart, Sun, Gem, ArrowRight, Star, Quote, Waves } from 'lucide-react';

import heroPanoramic from '@/assets/hero-panoramic.jpg';
import originalHeraPanorama from '@/assets/original-hera-panorama.jpg';
import originalHeraView from '@/assets/original-hera-view.jpg';
import originalHeraTerrace from '@/assets/original-hera-terrace.jpg';
import originalHeraLiving from '@/assets/original-hera-living.jpg';
import originalHeraBedroom from '@/assets/original-hera-bedroom.jpg';
import originalZeusPool from '@/assets/original-zeus-pool.jpg';
import originalZeusCourtyard from '@/assets/original-zeus-courtyard.jpg';
import originalZeusInterior from '@/assets/original-zeus-interior.jpg';
import originalZeusSunset from '@/assets/original-zeus-sunset.jpg';
import originalZeusLounge from '@/assets/original-zeus-lounge.jpg';
import originalZeusDining from '@/assets/original-zeus-dining.jpg';
import originalZeusView from '@/assets/original-zeus-view.jpg';
import hostDimitris from '@/assets/host-dimitris.jpg';
import balconyViewSivota from '@/assets/balcony-view-sivota.jpg';
import heraFeature from '@/assets/hera-feature.jpg';
import terraceDining from '@/assets/terrace-dining.jpg';

export default function About() {
  const { t } = useLanguage();

  return (
    <div>
      {/* ═══ HERO ═══ */}
      <section className="relative h-[85vh] flex items-end overflow-hidden">
        <img src={heroPanoramic} alt="Panoramic terrace overlooking the Ionian" className="absolute inset-0 w-full h-full object-cover hero-image-bright" width={1920} height={1080} fetchPriority="high" style={{ animation: 'heroZoom 25s ease-out forwards' }} />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 section-padding pb-16 lg:pb-24 w-full max-w-3xl">
          <p className="text-white/80 tracking-[0.4em] uppercase text-xs font-sans mb-4 drop-shadow-md">{t('ourStory')}</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-white drop-shadow-lg leading-[1.05]">
            {t('builtWithLove')}
          </h1>
        </div>
      </section>

      {/* ═══ OPENING ═══ */}
      <section className="section-padding py-24 lg:py-36">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <ScrollReveal>
            <div>
              <p className="text-luxury mb-6">{t('welcome')}</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-8 leading-tight">
                {t('notJustPlace')}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                {t('aboutIntro1')}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t('aboutIntro2')}
              </p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <div className="image-zoom aspect-[3/4] rounded-sm overflow-hidden">
              <img src={originalHeraTerrace} alt="Hera apartment terrace view" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={1067} />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ EDITORIAL GALLERY ═══ */}
      <section>
        <ScrollReveal>
          <div className="grid grid-cols-12 gap-[3px]">
            <div className="col-span-12 image-zoom aspect-[3/1] overflow-hidden">
              <img src={balconyViewSivota} alt="Balcony view over Sivota" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={1920} height={640} />
            </div>
            <div className="col-span-4 image-zoom aspect-[3/4] overflow-hidden">
              <img src={originalZeusPool} alt="Zeus pool" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={600} height={800} />
            </div>
            <div className="col-span-4 image-zoom aspect-[3/4] overflow-hidden">
              <img src={originalZeusCourtyard} alt="Stone courtyard" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={600} height={800} />
            </div>
            <div className="col-span-4 image-zoom aspect-[3/4] overflow-hidden">
              <img src={heraFeature} alt="Hera dining terrace" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={600} height={800} />
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ VALUES ═══ */}
      <section className="section-padding py-24 lg:py-36">
        <ScrollReveal>
          <div className="text-center mb-16">
            <p className="text-luxury mb-4">{t('whatSetsUsApart')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground">{t('moreThanAccommodation')}</h2>
          </div>
        </ScrollReveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {[
            { icon: Heart, title: t('handcrafted'), desc: t('handcraftedDesc') },
            { icon: Sun, title: t('bestViews'), desc: t('bestViewsDesc') },
            { icon: Waves, title: t('twoMinToBeach'), desc: t('twoMinToBeachDesc') },
            { icon: Gem, title: t('authentic'), desc: t('authenticDesc') },
          ].map((item, i) => (
            <ScrollReveal key={i} delay={i * 0.08}>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto mb-5">
                  <item.icon size={20} className="text-accent" aria-hidden="true" />
                </div>
                <h3 className="font-serif text-xl text-foreground mb-3">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* ═══ THE SETTING ═══ */}
      <section className="bg-secondary">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch">
          <ScrollReveal>
            <div className="image-zoom aspect-[4/3] lg:aspect-auto lg:min-h-[600px]">
              <img src={originalHeraLiving} alt="Hera apartment living room" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <div className="flex flex-col justify-center p-10 lg:p-16 xl:p-20 h-full">
              <p className="text-luxury mb-4">{t('theSetting')}</p>
              <h3 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-6">{t('everyMorningGift')}</h3>
              <p className="text-muted-foreground leading-relaxed mb-5">{t('settingDesc1')}</p>
              <p className="text-muted-foreground leading-relaxed">{t('settingDesc2')}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ THE LOCATION ═══ */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch">
          <ScrollReveal delay={0.15} className="order-2 lg:order-1">
            <div className="bg-secondary flex flex-col justify-center p-10 lg:p-16 xl:p-20 h-full">
              <p className="text-luxury mb-4">{t('beautyOfSivota')}</p>
              <h3 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-6">{t('greeceBestKeptSecret')}</h3>
              <p className="text-muted-foreground leading-relaxed mb-5">{t('locationDesc1')}</p>
              <p className="text-muted-foreground leading-relaxed">{t('locationDesc2')}</p>
            </div>
          </ScrollReveal>
          <ScrollReveal className="order-1 lg:order-2">
            <div className="image-zoom aspect-[4/3] lg:aspect-auto lg:min-h-[600px]">
              <img src={originalZeusSunset} alt="Sunset view from Zeus apartment" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ CINEMATIC QUOTE ═══ */}
      <section className="relative overflow-hidden">
        <ScrollReveal>
          <div className="image-zoom aspect-[21/9] lg:aspect-[3/1]">
            <img src={terraceDining} alt="Terrace dining" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={1920} height={640} />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-black/30 flex items-center justify-center">
            <div className="text-center max-w-3xl px-6">
              <Quote size={32} className="text-white/30 mx-auto mb-4" aria-hidden="true" />
              <p className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white drop-shadow-lg leading-tight">
                {t('quoteNotHotel')}
              </p>
              <p className="text-white/70 mt-6 text-sm tracking-[0.3em] uppercase">{t('ownerName')}</p>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ EDITORIAL GALLERY — Property details ═══ */}
      <section className="section-padding py-24 lg:py-36">
        <ScrollReveal>
          <div className="text-center mb-14">
            <p className="text-luxury mb-4">{t('theProperty')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground">{t('everyDetailConsidered')}</h2>
          </div>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <div className="grid grid-cols-12 gap-3 max-w-7xl mx-auto">
            <div className="col-span-7 row-span-2 image-zoom rounded-sm overflow-hidden">
              <img src={originalZeusLounge} alt="Zeus stone lounge" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
            </div>
            <div className="col-span-5 image-zoom aspect-[16/10] rounded-sm overflow-hidden">
              <img src={originalHeraBedroom} alt="Hera bedroom" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={500} />
            </div>
            <div className="col-span-5 image-zoom aspect-[16/10] rounded-sm overflow-hidden">
              <img src={originalZeusDining} alt="Zeus dining" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={500} />
            </div>
            <div className="col-span-12 image-zoom aspect-[21/9] rounded-sm overflow-hidden">
              <img src={originalHeraPanorama} alt="Hera panoramic terrace" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={1920} height={823} />
            </div>
            <div className="col-span-4 image-zoom aspect-square rounded-sm overflow-hidden">
              <img src={originalZeusInterior} alt="Zeus interior" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={600} height={600} />
            </div>
            <div className="col-span-4 image-zoom aspect-square rounded-sm overflow-hidden">
              <img src={originalZeusCourtyard} alt="Courtyard" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={600} height={600} />
            </div>
            <div className="col-span-4 image-zoom aspect-square rounded-sm overflow-hidden">
              <img src={originalZeusSunset} alt="Golden hour" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={600} height={600} />
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ MEET YOUR HOST ═══ */}
      <section className="bg-secondary">
        <div className="section-padding py-28 lg:py-40">
          <div className="max-w-5xl mx-auto">
            <ScrollReveal>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <div className="flex justify-center lg:justify-end">
                  <div className="relative">
                    <div className="w-72 h-72 md:w-80 md:h-80 rounded-full overflow-hidden shadow-2xl ring-4 ring-accent/20">
                      <img src={hostDimitris} alt="Dimitris Vlachos — Your Host" className="w-full h-full object-cover" loading="lazy" width={320} height={320} />
                    </div>
                    <div className="absolute -bottom-3 -right-3 bg-accent text-accent-foreground px-4 py-2 rounded-full text-xs tracking-wider uppercase font-medium shadow-lg">
                      {t('yourHost')}
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-luxury mb-4">{t('personalWelcome')}</p>
                  <h3 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-6">{t('meetDimitris')}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-5">{t('dimitrisQuote1')}</p>
                  <p className="text-muted-foreground leading-relaxed mb-5">{t('dimitrisQuote2')}</p>
                  <div className="flex items-center gap-2 mt-8">
                    <div className="flex gap-0.5" aria-label="5 stars">
                      {[1,2,3,4,5].map(i => <Star key={i} size={14} className="fill-golden text-golden" aria-hidden="true" />)}
                    </div>
                    <span className="text-sm text-muted-foreground italic">{t('dimitrisMadeFeel')}</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="relative py-32 lg:py-44">
        <img src={originalZeusPool} alt="" className="absolute inset-0 w-full h-full object-cover hero-image-bright" loading="lazy" width={1920} height={1080} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10" />
        <div className="relative z-10 text-center section-padding">
          <ScrollReveal>
            <p className="text-white/80 tracking-[0.3em] uppercase text-xs font-sans mb-6">{t('yourStoryStartsHere')}</p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-white mb-6 drop-shadow-lg leading-tight">
              {t('readyExperienceSivotaRoyal')}
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-lg mx-auto drop-shadow-md">
              {t('bookDirectlyBestRatesDimitris')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking" className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors">
                {t('checkAvailability')}
              </Link>
              <Link to="/contact" className="border border-white/40 text-white px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">
                {t('getInTouch')}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
